import {
  dayOfPhase,
  type GameEvent,
  type VictoryReason,
  type ViewerGameSnapshot,
  WOLF_ROLE_IDS,
} from "@werewolf/protocol";
import { Skull } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../api/client.ts";
import { Avatar } from "../components.tsx";
import { navigate } from "../routes.tsx";

const REPLAY_KINDS = [
  "player.eliminated",
  "players.finished_off",
  "phase.started",
  "vote.resolved",
  "night.resolved",
  "game.finished",
] as const;
type ReplayEvent = Extract<GameEvent, { kind: (typeof REPLAY_KINDS)[number] }>;

function isReplayEvent(event: GameEvent): event is ReplayEvent {
  return (REPLAY_KINDS as readonly string[]).includes(event.kind);
}

const REASON_KEYS: Record<VictoryReason, string> = {
  wolves_eliminated: "ui.over.reasonWolvesEliminated",
  village_eliminated: "ui.over.reasonVillageEliminated",
  veteran_lynched: "ui.over.reasonVeteranLynched",
  serial_killer_survives: "ui.over.reasonSerialKillerSurvives",
  cult_survives: "ui.over.reasonCultSurvives",
  stalemate: "ui.over.reasonStalemate",
  no_survivors: "ui.over.reasonNoSurvivors",
};

export function GameOverScreen({
  snapshot,
  events,
  replay = false,
}: {
  snapshot: ViewerGameSnapshot;
  events?: GameEvent[];
  /** True on the replay route, where "watch replay" would lead back here. */
  replay?: boolean;
}) {
  const { t } = useTranslation();
  const [loadedEvents, setLoadedEvents] = useState<GameEvent[]>(events ?? []);
  useEffect(() => {
    if (events !== undefined) return;
    void api.getReplay(snapshot.game.id).then((result) => setLoadedEvents(result.events));
  }, [events, snapshot.game.id]);
  const winner = snapshot.game.winner;
  const winnerFaction = winner?.winningFactions[0];
  const isDraw = winner !== undefined && winner.winningFactions.length === 0;
  const winTitleKey = isDraw
    ? "ui.over.drawTitle"
    : winnerFaction === "wolves"
      ? "ui.over.wolvesWinTitle"
      : winnerFaction === "village"
        ? "ui.over.villageWinsTitle"
        : winnerFaction === "veteran"
          ? "ui.over.veteranWinsTitle"
          : winnerFaction === "cult"
            ? "ui.over.cultWinsTitle"
            : "ui.over.serialKillerWinsTitle";
  const factionWinKey = isDraw
    ? "ui.over.drawFaction"
    : winnerFaction === "wolves"
      ? "ui.over.packWins"
      : winnerFaction === "village"
        ? "ui.over.villageWins"
        : winnerFaction === "veteran"
          ? "ui.over.veteranWins"
          : winnerFaction === "cult"
            ? "ui.over.cultWins"
            : "ui.over.serialKillerWins";
  const reasonKey = winner ? REASON_KEYS[winner.reason] : "ui.over.reasonSerialKillerSurvives";
  const publicEvents = loadedEvents.filter(
    (event): event is ReplayEvent => event.scope === "public" && isReplayEvent(event),
  );
  const names = new Map(snapshot.players.map((player) => [player.userId, player.displayName]));
  const line = (event: ReplayEvent) => {
    switch (event.kind) {
      case "player.eliminated":
        return t("events.public.player.eliminated", {
          player: names.get(event.payload.playerId) ?? event.payload.playerId,
          role: t(`roles.${event.payload.role}.name`),
        });
      case "players.finished_off":
        return t("events.public.players.finished_off", {
          faction: t(`factions.${event.payload.winningFaction}`),
          players: event.payload.playerIds
            .map((playerId) => names.get(playerId) ?? playerId)
            .join(", "),
        });
      case "phase.started":
        return t("events.public.phase.started", { phase: t(`phases.${event.payload.type}`) });
      case "vote.resolved":
        return t("events.public.vote.resolved", event.payload);
      case "night.resolved":
        return t("events.public.night.resolved.count", { count: event.payload.deaths.length });
      case "game.finished":
        return t("events.public.game.finished", {
          faction: t(factionWinKey),
        });
    }
  };
  const wolves = snapshot.players.filter(
    (player) =>
      player.revealedRole && (WOLF_ROLE_IDS as readonly string[]).includes(player.revealedRole),
  ).length;
  const villagers = snapshot.players.length - wolves;
  return (
    <div className="flex min-h-0 flex-1 overflow-y-auto flex-col gap-5.5 bg-[radial-gradient(100%_45%_at_50%_0%,rgba(179,58,54,.18),transparent_70%)] px-4.5 pb-5 pt-9">
      <section className="flex flex-col items-center gap-3.5 text-center">
        <span className="h-14 w-14 rounded-full bg-blood shadow-[0_0_60px_rgba(179,58,54,.5)]" />
        <div>
          <p className="eyebrow text-blood-light">{t(reasonKey)}</p>
          <h1 className="mt-2 text-[36px] font-semibold tracking-[-0.035em]">{t(winTitleKey)}</h1>
        </div>
        <p className="max-w-70 text-sm text-fog">
          {t("ui.over.summary", {
            wolves: t("ui.over.wolvesCount.count", { count: wolves }),
            villagers: t("ui.over.villagersCount.count", { count: villagers }),
            nights: t("ui.over.nightsCount.count", { count: snapshot.game.day }),
          })}
        </p>
      </section>
      <section className="flex flex-col gap-2.5">
        <p className="eyebrow">{t("ui.over.rolesRevealed")}</p>
        {snapshot.players.map((player) => {
          const wolf =
            player.revealedRole !== undefined &&
            (WOLF_ROLE_IDS as readonly string[]).includes(player.revealedRole);
          const dead = player.status === "dead";
          const winner = snapshot.game.winner?.winningPlayers.includes(player.userId) ?? false;
          return (
            <div
              className={`row relative overflow-hidden ${wolf ? "border-blood/35 bg-blood/10" : ""}`}
              key={player.userId}
            >
              {winner && (
                <span
                  aria-label={t("ui.over.winner")}
                  className="absolute -left-8 top-4 z-10 flex h-5 w-24 -rotate-45 items-center justify-center border-y border-night/30 bg-bone text-xs font-bold text-slate-dark shadow-md"
                  role="img"
                >
                  W
                </span>
              )}
              <span className="relative">
                <Avatar dead={dead} name={player.displayName} />
                {dead && (
                  <Skull
                    aria-label={t("playerStatuses.dead")}
                    className="absolute inset-0 z-20 m-auto h-4.5 w-4.5 text-bone"
                    role="img"
                  />
                )}
              </span>
              <span className="row__name text-[17px]">
                {player.displayName}
                {player.userId === snapshot.me?.userId && (
                  <span className="ml-1.5 font-mono text-[11px] text-sage-light">
                    {t("ui.you").toUpperCase()}
                  </span>
                )}
              </span>
              <span
                className={`font-mono text-xs ${wolf ? "text-blood-light" : "text-sage-light"}`}
              >
                {player.revealedRole ? t(`roles.${player.revealedRole}.name`) : ""}
              </span>
            </div>
          );
        })}
      </section>
      <section className="flex flex-col gap-2.5">
        <p className="eyebrow">{t("ui.over.replay")}</p>
        <ul className="card flex flex-col gap-2 p-2">
          {publicEvents.map((event) => (
            <li className="flex gap-3.5 px-2 py-2 text-sm text-paper-dim" key={event.id}>
              <span className="w-9 flex-none font-mono text-[11px] text-fog-dim">
                {event.kind === "phase.started" ? `D${dayOfPhase(event.payload.phaseId)}` : "·"}
              </span>
              {line(event)}
            </li>
          ))}
        </ul>
      </section>
      <div className="mt-auto flex gap-2.5">
        {!replay && (
          <button
            className="btn btn--ghost min-h-14 flex-1"
            onClick={() => navigate(`/games/${snapshot.game.id}/replay`)}
            type="button"
          >
            {t("ui.over.watchReplay")}
          </button>
        )}
        <button
          className="btn btn--primary min-h-14 flex-1"
          onClick={() => navigate("/create")}
          type="button"
        >
          {t("ui.over.newGame")}
        </button>
      </div>
    </div>
  );
}
